import {
  Component,
  computed,
  input,
  Signal,
  signal,
  output,
  effect,
  LOCALE_ID,
  ViewChild,
  inject,
  DEFAULT_CURRENCY_CODE,
  ContentChild,
  TemplateRef,
  untracked,
  ContentChildren,
  QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CompositeFilterDescriptor, DataResult, process, State } from '@progress/kendo-data-query';
import {
  GridModule,
  DataStateChangeEvent,
  RowClassArgs,
  CellClickEvent,
  KENDO_GRID_EXCEL_EXPORT,
  KENDO_GRID_PDF_EXPORT,
  PDFModule,
  PDFService,
  ContextService,
  ExcelService,
  SelectableSettings,
  SelectionEvent,
  ExcelExportEvent,
  GridComponent,
  DetailExpandEvent,
} from '@progress/kendo-angular-grid';
import { ExcelExportData, ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { fileExcelIcon, filePdfIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { KENDO_PDFEXPORT, PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { MessageService } from '@progress/kendo-angular-l10n';
import { IntlModule, CldrIntlService, IntlService } from '@progress/kendo-angular-intl';
import { DruckDatum } from './funktionen/details';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { GrunddatenService } from '../../grundlagen/grunddaten.service';
import { TooltipModule, TooltipDirective } from '@progress/kendo-angular-tooltip';
import { Injectable } from '@angular/core';
import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import saveAs from 'file-saver';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';

@Injectable({
  providedIn: 'root',
})
export class CustomMessagesService extends MessageService {
  private messages: { [key: string]: string } = {
    'kendo.grid.filterIsTrue': 'Ja',
    'kendo.grid.filterIsFalse': 'Nein',
    'kendo.grid.filterBooleanAll': '(Alle)',
  };

  public override get(key: string): string {
    return this.messages[key] || super.get(key);
  }
}

@Component({
  selector: 'ui-custom-kendo-grid',
  standalone: true,
  imports: [
    CommonModule,
    GridModule,
    ExcelExportModule,
    PDFModule,
    ButtonsModule,
    KENDO_GRID_EXCEL_EXPORT,
    KENDO_GRID_PDF_EXPORT,
    KENDO_PDFEXPORT,
    IntlModule,
    DateInputsModule,
    TooltipModule,
    FormsModule,
    DropDownListModule,
  ],
  providers: [
    PDFService,
    ExcelService,
    ContextService,
    { provide: MessageService, useClass: CustomMessagesService },
    { provide: IntlService, useClass: CldrIntlService },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: 'KENDO_GRID_USE_INTL_SERVICE', useValue: true },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
  ],
  template: `
    <div kendoTooltip [tooltipTemplate]="tooltipTemplate" showOn="hover" [filter]="tooltipFilter()" (mouseover)="showTooltip($event)" (focus)="showTooltip($event)">
      <kendo-grid
        [data]="gridView()"
        [pageSize]="state().take"
        [skip]="state().skip"
        [filterable]="true"
        [(filter)]="state().filter"
        (dataStateChange)="onStateChange($event)"
        (filterChange)="onFilterChange($event)"
        [(sort)]="state().sort"
        [sortable]="{ allowUnsort: true, mode: 'single' }"
        [resizable]="true"
        [pageable]="{ pageSizes: [15, 20, 25, 50, 100] }"
        [selectable]="selectable()"
        [kendoGridSelectBy]="selectBy()"
        [selectedKeys]="selectedKeysIn() ?? []"
        (selectionChange)="onSelectionChange($event)"
        [rowClass]="rowClassMerged"
        (cellClick)="onGridCellClick($event)"
        (detailExpand)="detailExpand.emit($event)"
        (excelExport)="onExcelExport($event)"
      >
        <kendo-grid-messages
          noRecords="Keine Datensätze verfügbar."
          pagerFirstPage="Zur ersten Seite"
          pagerLastPage="Zur letzten Seite"
          pagerNextPage="Zur nächsten Seite"
          pagerPreviousPage="Zur vorherigen Seite"
          pagerPage="Seite"
          pagerOf="von"
          pagerItemsPerPage="Einträge pro Seite"
          pagerItems="Einträgen"
          filter="Filter"
        >
        </kendo-grid-messages>

        @if (dateiName() !== '') {
          <ng-template kendoGridToolbarTemplate>
            <div style="display: flex; justify-content: flex-end; width: 100%">
              <span
                class="me-2"
                [title]="pdfExportGesperrt() ? 'PDF-Export bei mehr als ' + maxPdfZeilen + ' Zeilen nicht verfügbar – bitte Excel-Export verwenden oder Filter setzen.' : 'Export als PDF'"
              >
                <button class="btnBlau" kendoButton type="button" [disabled]="pdfExportGesperrt()" (click)="exportPdf()">Export PDF</button>
              </span>
              <kendo-button class="btnBlau" kendoGridExcelCommand [svgIcon]="fileExcelIcon">Export Excel</kendo-button>
            </div>
          </ng-template>
        }
        @for (col of columnsWithDefaults(); track $index) {
          <kendo-grid-column
            [field]="col.field"
            [title]="col.title"
            [width]="col.width"
            [autoSize]="autoSizeColumn()"
            [minResizableWidth]="col.width"
            [format]="col.format"
            [hidden]="col.hidden"
            [sortable]="true"
            [filterable]="col.filterbar ?? true"
            [headerClass]="col.edit ? 'editable-header ' + (col.css || '') : col.css"
            [class]="col.css"
          >
            <ng-template kendoGridHeaderTemplate>
              <span kendoTooltip [title]="col.tooltip ? col.tooltip : col.title">{{ col.title }}</span>
            </ng-template>

            @if (col.html) {
              <ng-template kendoGridCellTemplate let-dataItem>
                <div
                  [ngClass]="{ 'marked-grid-text': col.markieren }"
                  [innerHTML]="
                    (dataItem[col.anzeigeFeld || col.field] ?? '')
                      .toString()
                      .replaceAll(
                        '
',
                        '<br />'
                      )
                      .replaceAll(
                        '
',
                        '<br />'
                      )
                  "
                ></div>
              </ng-template>
            } @else {
              @if (col.type === 'button') {
                <ng-template kendoGridCellTemplate let-dataItem>
                  <button kendoButton>{{ col.title }}</button>
                </ng-template>
              } @else if (col.type === 'boolean') {
                <ng-template kendoGridCellTemplate let-dataItem>
                  <div [ngClass]="{ 'marked-grid-text': col.markieren, 'editable-cell': col.edit && (!col.editWenn || dataItem[col.editWenn]) }" class="text-center">
                    @if (col.edit && (!col.editWenn || dataItem[col.editWenn])) {
                      <input type="checkbox" [(ngModel)]="dataItem[col.field]" (ngModelChange)="onCellValueChange(col.field, dataItem, $event)" />
                    } @else if (!col.editWenn) {
                      {{ dataItem[col.field] ? 'Ja' : 'Nein' }}
                    }
                  </div>
                </ng-template>
              } @else if (col.type === 'number' && col.edit) {
                <ng-template kendoGridCellTemplate let-dataItem>
                  <div class="editable-cell" [ngClass]="{ 'marked-grid-text': col.markieren }">
                    <input
                      type="number"
                      class="k-textbox k-input k-input-md k-rounded-md k-input-solid"
                      [(ngModel)]="dataItem[col.field]"
                      (ngModelChange)="onCellValueChange(col.field, dataItem, $event)"
                      style="width: 100%; border: 1px solid #ccc; padding: 4px;"
                    />
                  </div>
                </ng-template>
              } @else if (col.type === 'string' && col.edit) {
                <ng-template kendoGridCellTemplate let-dataItem>
                  <div class="editable-cell" [ngClass]="{ 'marked-grid-text': col.markieren }">
                    <input
                      type="text"
                      class="k-textbox k-input k-input-md k-rounded-md k-input-solid"
                      [(ngModel)]="dataItem[col.field]"
                      (ngModelChange)="onCellValueChange(col.field, dataItem, $event)"
                      style="width: 100%; border: 1px solid #ccc; padding: 4px;"
                    />
                  </div>
                </ng-template>
              } @else if (col.type === 'date' && col.edit) {
                <ng-template kendoGridCellTemplate let-dataItem>
                  <div class="editable-cell" [ngClass]="{ 'marked-grid-text': col.markieren }">
                    <kendo-datepicker [(value)]="dataItem[col.field]" (valueChange)="onCellValueChange(col.field, dataItem, $event)" [format]="col.format || 'dd.MM.yyyy'"> </kendo-datepicker>
                  </div>
                </ng-template>
              } @else {
                <ng-template kendoGridCellTemplate let-dataItem>
                  <div [ngClass]="{ 'marked-grid-text': col.markieren }">
                    @if (col.type === 'date' && col.format) {
                      {{ dataItem[col.field] | date: col.format }}
                    } @else if (col.type === 'number' && col.numberFormat === 'currency') {
                      {{ dataItem[col.field] | currency: 'EUR' : 'symbol' : '1.2-2' : 'de-DE' }}
                    } @else if (col.type === 'number' && col.numberFormat === 'decimal') {
                      {{ dataItem[col.field] | number: '1.2-2' : 'de-DE' }}
                    } @else if (col.type === 'number' && col.numberFormat === 'tausend') {
                      {{ dataItem[col.field] | number: '1.0-0' : 'de-DE' }}
                    } @else {
                      {{ dataItem[col.field] }}
                    }
                  </div>
                </ng-template>
              }
            }

            @if (hasAggregate(col.field)) {
              <ng-template kendoGridFooterTemplate>
                <div [ngClass]="col.css" style="font-weight: bold;">
                  @if (col.type === 'number' && col.numberFormat === 'currency') {
                    {{ aggregates()[col.field] | currency: 'EUR' : 'symbol' : '1.2-2' : 'de-DE' }}
                  } @else if (col.type === 'number' && col.numberFormat === 'decimal') {
                    {{ aggregates()[col.field] | number: '1.2-2' : 'de-DE' }}
                  } @else {
                    {{ aggregates()[col.field] }}
                  }
                </div>
              </ng-template>
            } @else if (isFirstColumn(col.field) && aggregateColumns().length > 0) {
              <ng-template kendoGridFooterTemplate>
                <div style="font-weight: bold;">Gesamtwert</div>
              </ng-template>
            }
            @switch (col.type) {
              @case ('string') {
                <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
                  <kendo-grid-string-filter-cell [column]="column" [filter]="filter" [ngClass]="{ 'active-filter': isFilterActive(column.field) }">
                    <kendo-filter-eq-operator text="Ist gleich"></kendo-filter-eq-operator>
                    <kendo-filter-neq-operator text="Ist ungleich"></kendo-filter-neq-operator>
                    <kendo-filter-contains-operator text="Enthält"></kendo-filter-contains-operator>
                    <kendo-filter-startswith-operator text="Beginnt mit"></kendo-filter-startswith-operator>
                    <kendo-filter-endswith-operator text="Endet mit"></kendo-filter-endswith-operator>
                  </kendo-grid-string-filter-cell>
                </ng-template>
              }
              @case ('number') {
                <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
                  <kendo-grid-numeric-filter-cell [column]="column" [filter]="filter" [spinners]="false" format="0" [ngClass]="{ 'active-filter': isFilterActive(column.field) }">
                    <kendo-filter-eq-operator text="Ist gleich"></kendo-filter-eq-operator>
                    <kendo-filter-neq-operator text="Ist ungleich"></kendo-filter-neq-operator>
                    <kendo-filter-gt-operator text="Größer als"></kendo-filter-gt-operator>
                    <kendo-filter-gte-operator text="Größer gleich"></kendo-filter-gte-operator>
                    <kendo-filter-lt-operator text="Kleiner als"></kendo-filter-lt-operator>
                    <kendo-filter-lte-operator text="Kleiner gleich"></kendo-filter-lte-operator>
                  </kendo-grid-numeric-filter-cell>
                </ng-template>
              }
              @case ('date') {
                <ng-template kendoGridFilterCellTemplate let-filter let-column="column" let-filterService="filterService">
                  <kendo-grid-date-filter-cell [column]="column" [filter]="filter" placeholder="" [ngClass]="{ 'active-filter': isFilterActive(column.field) }">
                    <kendo-filter-eq-operator text="Ist gleich"></kendo-filter-eq-operator>
                    <kendo-filter-neq-operator text="Ist ungleich"></kendo-filter-neq-operator>
                    <kendo-filter-gt-operator text="Nach"></kendo-filter-gt-operator>
                    <kendo-filter-gte-operator text="Nach oder gleich"></kendo-filter-gte-operator>
                    <kendo-filter-lt-operator text="Vor"></kendo-filter-lt-operator>
                    <kendo-filter-lte-operator text="Vor oder gleich"></kendo-filter-lte-operator>
                  </kendo-grid-date-filter-cell>
                </ng-template>
              }
              @case ('boolean') {
                <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
                  <kendo-grid-boolean-filter-cell [column]="column" [filter]="filter" [ngClass]="{ 'active-filter': isFilterActive(column.field) }"> </kendo-grid-boolean-filter-cell>
                </ng-template>
              }
              @case ('textdropdown') {
                <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
                  <kendo-dropdownlist
                    [data]="[ALLE, ...(uniqueColumnValues()[column.field] ?? [])]"
                    [valuePrimitive]="true"
                    [value]="getDropdownFilterValue(filter, column.field)"
                    (valueChange)="onDropdownFilterChange($event, column.field)"
                    [popupSettings]="{ width: getDropdownPopupWidth(column.field) }"
                    [ngClass]="{ 'active-filter': isFilterActive(column.field) }"
                    style="width: 100%"
                  >
                  </kendo-dropdownlist>
                </ng-template>
              }
              @case ('button') {}
            }
          </kendo-grid-column>
        }
        @if (actionTemplate) {
          <kendo-grid-column title="" [width]="80" [filterable]="false" [sortable]="false">
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <ng-container *ngTemplateOutlet="actionTemplate; context: { $implicit: dataItem, rowIndex: rowIndex }"></ng-container>
            </ng-template>
          </kendo-grid-column>
        }
        @if (detailTemplate) {
          <ng-template kendoGridDetailTemplate let-dataItem>
            <ng-container *ngTemplateOutlet="detailTemplate; context: { $implicit: dataItem }"></ng-container>
          </ng-template>
        }
        @if (dateiName() !== '' && pdfExportAktiv()) {
          <div style="position: fixed; left: -10000px; top: -10000px">
            <kendo-grid #pdfGrid [data]="gefilterteZeilen()" [sortable]="false" [filterable]="false" [pageable]="false">
              @for (col of exportableColumns(); track $index) {
                <kendo-grid-column [field]="col.field" [title]="col.title" [width]="col.width" [format]="col.format">
                  @if (hasAggregate(col.field)) {
                    <ng-template kendoGridFooterTemplate>
                      <div style="font-weight: bold;">
                        @if (col.type === 'number' && col.numberFormat === 'currency') {
                          {{ aggregates()[col.field] | currency: 'EUR' : 'symbol' : '1.2-2' : 'de-DE' }}
                        } @else if (col.type === 'number' && col.numberFormat === 'decimal') {
                          {{ aggregates()[col.field] | number: '1.2-2' : 'de-DE' }}
                        } @else {
                          {{ aggregates()[col.field] }}
                        }
                      </div>
                    </ng-template>
                  } @else if (isFirstColumn(col.field) && aggregateColumns().length > 0) {
                    <ng-template kendoGridFooterTemplate>
                      <div style="font-weight: bold;">Gesamtwert</div>
                    </ng-template>
                  }
                </kendo-grid-column>
              }

              <kendo-grid-pdf #pdfExport fileName="{{ dateiNameKomplett() }}.pdf" [allPages]="true" paperSize="A4" [repeatHeaders]="true" [landscape]="true" [scale]="0.5">
                <kendo-grid-pdf-margin top="2cm" left="1cm" right="1cm" bottom="1cm"></kendo-grid-pdf-margin>

                <ng-template kendoGridPDFTemplate let-pageNum="pageNum" let-totalPages="totalPages">
                  <div class="page-template">
                    <div class="header">
                      <h3>{{ dateiNameKomplett() }}</h3>
                    </div>
                    <div class="footer">{{ DruckDatum }} - Seite {{ pageNum }} von {{ totalPages }}</div>
                  </div>
                </ng-template>
              </kendo-grid-pdf>
            </kendo-grid>
          </div>
        }

        @if (dateiName() !== '') {
          <kendo-grid-excel #excelComp [fetchData]="fetchFilteredData" fileName="{{ dateiNameKomplett() }}.xlsx">
            @for (col of exportableColumns(); track $index) {
              <kendo-excelexport-column [field]="col.field" [title]="col.title"> </kendo-excelexport-column>
            }
          </kendo-grid-excel>
        }
      </kendo-grid>

      <ng-template #tooltipTemplate let-anchor>
        <div [innerHTML]="getTooltipContent(anchor)"></div>
      </ng-template>
    </div>
  `,
  styles: [
    `
      :host ::ng-deep .k-grid tr.grid-treffer > td,
      :host ::ng-deep .k-grid .k-table-row.grid-treffer > .k-table-td {
        font-weight: bold;
      }

      :host ::ng-deep .k-tooltip {
        z-index: 10000;
      }

      :host ::ng-deep .k-grid {
        font-family: 'Segoe UI' !important;
        letter-spacing: -0.5px; /* macht Schrift optisch schmaler */
      }

      :host ::ng-deep .k-grid td > div:not(.editable-cell) {
        pointer-events: none;
      }

      :host ::ng-deep .k-grid td > div.editable-cell,
      :host ::ng-deep .k-grid td > div.editable-cell * {
        pointer-events: auto !important;
      }

      :host ::ng-deep .k-grid .k-table-th.editable-header,
      :host ::ng-deep .k-grid th.editable-header {
        background-color: #e0f7fa !important;
        font-weight: bold;
      }

      .active-filter {
        border: 2px solid #2cbb00ff !important;
        border-radius: 4px;
      }

      :host ::ng-deep .k-grid .k-table-row.k-selected > .k-table-td,
      :host ::ng-deep .k-grid tr.k-selected > td {
        background-color: #524d4dff !important;
        color: yellow !important;
      }

      /* Optional: darker on hover/focus */
      :host ::ng-deep .k-grid .k-table-row.k-selected.k-hover > .k-table-td,
      :host ::ng-deep .k-grid tr.k-selected.k-hover > td {
        background-color: #e2e2e2 !important;
      }
      /* Zeige nur Filter-Icons für aktive Filter */
      :host ::ng-deep .k-grid-header .k-filtercell .k-filtercell-operator {
        display: none;
      }
      /* Zeige Operator nur wenn Parent die active-filter Klasse hat */
      :host ::ng-deep .k-grid-header .k-filtercell.active-filter kendo-grid-filter-cell-operators {
        display: block;
      }

      /* PDF Export Styles */

      .page-template {
        font-family: 'DejaVu Sans', 'Arial', sans-serif;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }

      .page-template .header {
        position: absolute;
        top: 30px;
        left: 30px;
        right: 30px;
        border-bottom: 1px solid #333;
        color: #333;
      }

      .page-template .footer {
        position: absolute;
        bottom: 30px;
        left: 30px;
        right: 30px;
        border-top: 1px solid #333;
        text-align: center;
        color: #333;
      }

      @font-face {
        font-family: 'DejaVu Sans';
        src: url('/assets/fonts/DejaVuSans.ttf') format('truetype');
      }
      @font-face {
        font-family: 'DejaVu Sans';
        font-weight: bold;
        src: url('/assets/fonts/DejaVuSans-Bold.ttf') format('truetype');
      }
      @font-face {
        font-family: 'DejaVu Sans';
        font-style: italic;
        src: url('/assets/fonts/DejaVuSans-Oblique.ttf') format('truetype');
      }
      @font-face {
        font-family: 'DejaVu Sans';
        font-weight: bold;
        font-style: italic;
        src: url('/assets/fonts/DejaVuSans-Oblique.ttf') format('truetype');
      }

      /* DejaVu Sans nur für PDF-Export, nicht für die Grid-Ansicht */
      :host ::ng-deep .k-pdf-export .k-grid {
        font-family: 'DejaVu Sans', 'Arial', sans-serif;
      }
    `,
  ],
})
export class CustomKendoGridComponent {
  private stateRestored = false;

  protected readonly maxPdfZeilen = 1000;

  private grunddaten = inject(GrunddatenService);
  constructor() {
    effect(() => {
      const currentData = this.processedData();
      if (!this.stateRestored && currentData.length > 0 && this.gridId() !== '') {
        const sm = this.grunddaten.loadParameter('grid_' + this.gridId());
        if (sm) {
          const restoredFilter = untracked(() => this.reviveDateFilters(sm.filter));
          // Berechne die Gesamtzahl der gefilterten Daten
          const totalItems = process(currentData, {
            filter: restoredFilter,
            sort: sm.sort ?? [],
            skip: 0,
            take: Number.MAX_SAFE_INTEGER,
          }).total;
          const take = sm.take ?? this.grunddaten.gridPagesize();
          const totalPages = Math.ceil(totalItems / take); // Gesamtzahl der Seiten
          const currentPage = Math.floor((sm.skip ?? 0) / take) + 1; // Aktuelle Seite basierend auf sm.skip

          // Wenn die aktuelle Seite größer als die Gesamtzahl der Seiten ist, setze skip auf 0 (Seite 1)
          const adjustedSkip = currentPage > totalPages ? 0 : (sm.skip ?? 0);

          this._state.set({
            skip: adjustedSkip,
            take: sm.take ?? this.grunddaten.gridPagesize(),
            sort: sm.sort ?? [],
            filter: restoredFilter,
          });
        }
        this.stateRestored = true;
      }
    });

    effect(() => {
      const ids = this.gefilterteZeilen().map((z) => z.id?.toString() ?? '');
      this.angezeigteIds.emit(ids);
      // console.log('tooltipFilter:', this.tooltipFilter()); // Debug tooltip filter
    });

    effect(() => {
      const field = this.emitField();
      if (!field) {
        return;
      }

      const values = this.gefilterteZeilen().map((z) => {
        const v = z?.[field];
        return v != null ? v.toString() : '';
      });
      this.angezeigteSpaltenwerte.emit(values);
    });
  }

  private reviveDateFilters(node: CompositeFilterDescriptor | undefined): CompositeFilterDescriptor {
    if (!node) {
      return { logic: 'and', filters: [] };
    }

    const dateFields = new Set(
      this.columnsWithDefaults()
        .filter((c) => c.type === 'date')
        .map((c) => c.field),
    );

    const walk = (n: any): any => {
      if ('filters' in n) {
        return { ...n, filters: (n.filters ?? []).map(walk) };
      }
      if (dateFields.has(n.field) && typeof n.value === 'string' && n.value !== '') {
        const d = new Date(n.value);
        return isNaN(d.getTime()) ? n : { ...n, value: d };
      }
      return n;
    };

    return walk(node);
  }

  @ViewChild(TooltipDirective) public tooltipDir!: TooltipDirective;

  @ViewChild(GridComponent) grid!: GridComponent;

  @ViewChild('pdfGrid') pdfGrid!: GridComponent;
  @ViewChild('pdfExport') pdfExport!: PDFExportComponent;

  /** Alle direkt übergebenen Templates (ohne verschachtelte, z.B. aus einem inneren Grid im Detailbereich) */
  @ContentChildren(TemplateRef, { descendants: false }) private vorlagen?: QueryList<TemplateRef<any>>;

  /** Optional: Detailbereich zum Aufklappen, nur wenn ausdrücklich mit #detail markiert */
  @ContentChild('detail', { read: TemplateRef, descendants: false }) detailTemplate?: TemplateRef<any>;

  /** Aktionsspalte: wie bisher das (erste) übergebene Template – außer dem #detail-Template */
  get actionTemplate(): TemplateRef<any> | undefined {
    return this.vorlagen?.find((t) => t !== this.detailTemplate);
  }

  private messe<T>(name: string, fn: () => T): T {
    const t0 = performance.now();
    const result = fn();
    const dauer = performance.now() - t0;
    if (dauer > 50) {
      console.warn(`[Grid ${this.gridId()}] ${name}: ${dauer.toFixed(0)} ms`);
    }
    return result;
  }

  protected pdfExportAktiv = signal(false);
  protected pdfExportGesperrt = computed(() => this.gefilterteZeilen().length > this.maxPdfZeilen);

  private paintAbwarten(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 0))));
  }

  async exportPdf(): Promise<void> {
    this.grunddaten.waitCursor.set(true);
    await this.paintAbwarten();
    this.pdfExportAktiv.set(true);
    await this.paintAbwarten();
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r))));

    try {
      const element = this.pdfGrid.wrapper.nativeElement;
      const group = await drawDOM(element, {
        paperSize: 'A4',
        landscape: true,
        repeatHeaders: true,
        scale: 0.5,
        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
      });
      const dataUri = await exportPDF(group);
      saveAs(dataUri, `${this.dateiNameKomplett()}.pdf`);
    } finally {
      this.pdfExportAktiv.set(false);
      this.grunddaten.waitCursor.set(false);
    }
  }

  private hiddenBackup = new Map<string, boolean>();

  onPdfExport() {
    const cols = this.columnsWithDefaults();
    cols.forEach((c) => {
      if (c.type === 'button') {
        this.hiddenBackup.set(c.field, c.hidden);
        c.hidden = true;
      }
    });

    // eslint-disable-next-line no-restricted-globals
    setTimeout(() => {
      cols.forEach((c) => {
        if (this.hiddenBackup.has(c.field)) {
          c.hidden = this.hiddenBackup.get(c.field)!;
        }
      });
      this.hiddenBackup.clear();
    });
  }

  // Inputs
  // multiselect:
  selectBy = input<string>('id');
  selectedKeysIn = signal<any[] | null>(null);
  selectable = input<SelectableSettings>({ mode: 'single' });

  emitField = input<string>('id');

  gridId = input<string>('');
  data = input<any[]>([]);

  protected readonly ALLE = '(Alle)';

  uniqueColumnValues = computed(() =>
    this.messe('uniqueColumnValues', () => {
      const cols = this.columnsWithDefaults().filter((c) => c.type === 'textdropdown');
      const data = this.processedData();
      const map: { [field: string]: string[] } = {};

      cols.forEach((col) => {
        const values = new Set<string>();
        data.forEach((item) => {
          const v = item[col.field];
          if (v !== null && v !== undefined && v !== '') {
            values.add(String(v));
          }
        });
        map[col.field] = Array.from(values).sort((a, b) => a.localeCompare(b, 'de'));
      });

      return map;
    }),
  );

  getDropdownFilterValue(filter: CompositeFilterDescriptor, field: string): string {
    const found = (filter?.filters ?? []).find((f: any) => 'field' in f && f.field === field);
    return found ? String((found as any).value) : this.ALLE;
  }

  onDropdownFilterChange(value: string, field: string): void {
    const col = this.columnsWithDefaults().find((c) => c.field === field);
    const castValue = col?.type === 'number' ? Number(value) : value;

    this._state.update((s) => {
      const currentFilters = (s.filter?.filters ?? []).filter((f: any) => !('field' in f) || f.field !== field);
      const newFilters = value && value !== this.ALLE ? [...currentFilters, { field, operator: 'eq', value: castValue }] : currentFilters;

      return {
        ...s,
        filter: { logic: s.filter?.logic ?? 'and', filters: newFilters },
      };
    });

    if (this.gridId() !== '') {
      this.grunddaten.saveParameter('grid_' + this.gridId(), this._state());
    }
  }

  getDropdownPopupWidth(field: string): number {
    const values = this.uniqueColumnValues()[field] ?? [];
    const longest = values.reduce((max, v) => Math.max(max, v.length), this.ALLE.length);
    return Math.min(Math.max(longest * 7.5 + 40, 120), 400);
  }

  onExcelExport(e: unknown): void {
    this.grunddaten.waitCursor.set(true);
    const ev = e as ExcelExportEvent;
    const sheet = ev.workbook.sheets[0];

    // Remove button columns from export
    const cols = this.columnsWithDefaults();
    const removeVisibleIndexes = cols
      .map((c, i) => ({ c, i }))
      .filter((x) => !x.c.hidden && x.c.type === 'button')
      .map((x) => this.visibleColumnIndices().indexOf(x.i))
      .filter((i) => i >= 0)
      .sort((a, b) => b - a);

    if (sheet.columns) {
      removeVisibleIndexes.forEach((idx) => sheet.columns!.splice(idx, 1));
    }

    sheet.rows?.forEach((r) => {
      if (r.cells) {
        removeVisibleIndexes.forEach((idx) => r.cells!.splice(idx, 1));
      }
    });

    // Add aggregate footer row if aggregates exist
    if (this.aggregateColumns().length > 0 && sheet.rows) {
      const exportCols = this.exportableColumns();
      const footerRow: any = {
        type: 'footer',
        cells: exportCols.map((col) => {
          if (this.isFirstColumn(col.field)) {
            return {
              value: 'Gesamtwert',
              bold: true,
            };
          } else if (this.hasAggregate(col.field)) {
            const aggValue = this.aggregates()[col.field];
            return {
              value: aggValue,
              bold: true,
              format: col.numberFormat === 'currency' ? '#,##0.00 €' : col.numberFormat === 'decimal' ? '#,##0.00' : undefined,
            };
          } else {
            return { value: '' };
          }
        }),
      };
      sheet.rows.push(footerRow);
    }

    this.grunddaten.waitCursor.set(false);
  }

  trefferText = input<string>('');

  private trefferFelder = computed(() =>
    this.columnsWithDefaults()
      .filter((c) => !c.hidden && c.type !== 'button')
      .map((c) => c.field),
  );

  private istTreffer(item: any): boolean {
    const text = this.trefferText().trim().toLowerCase();
    if (text === '' || !item) return false;
    return this.trefferFelder().some((f) =>
      String(item[f] ?? '')
        .toLowerCase()
        .includes(text),
    );
  }

  private processedData = computed(() =>
    this.messe('processedData', () => {
      const rawData = this.data();
      const cols = this.columns();

      const dateFields = cols.filter((col) => col.type === 'date').map((col) => col.field);

      if (dateFields.length === 0) {
        return rawData;
      }

      return rawData.map((item) => {
        const converted = { ...item };
        dateFields.forEach((field) => {
          if (converted[field] && !(converted[field] instanceof Date)) {
            converted[field] = new Date(converted[field]);
          }
        });
        return converted;
      });
    }),
  );

  columns = input<
    {
      field: string;
      title?: string;
      width?: number;
      format?: string;
      hidden?: boolean;
      html?: boolean;
      type?: string;
      markieren?: boolean;
      tooltip?: string;
      css?: string;
      numberFormat?: 'currency' | 'decimal' | 'tausend';
      edit?: boolean;
      anzeigeFeld?: string;
      editWenn?: string;
      filterbar?: boolean;
    }[]
  >([]);
  dateiName = input<string>('');
  autoSizeColumn = input<boolean>(false);
  rowCallback = input<(ctx: RowClassArgs) => { [key: string]: boolean }>((ctx) => ({
    zebraEven: ctx.index % 2 === 0,
    zebraOdd: ctx.index % 2 !== 0,
  }));
  aggregateColumns = input<string[]>([]);

  editMode = input<'emit' | 'direct'>('emit');

  aggregates = computed(() => {
    const aggCols = this.aggregateColumns();
    if (aggCols.length === 0) return {};

    const data = this.gefilterteZeilen();
    const result: { [key: string]: number } = {};

    aggCols.forEach((field) => {
      const sum = data.reduce((acc, row) => {
        const value = row[field];
        return acc + (typeof value === 'number' ? value : 0);
      }, 0);
      result[field] = sum;
    });

    return result;
  });

  hasAggregate(field: string): boolean {
    return this.aggregateColumns().includes(field);
  }

  isFirstColumn(field: string): boolean {
    const visibleCols = this.columnsWithDefaults().filter((c) => !c.hidden);
    return visibleCols.length > 0 && visibleCols[0].field === field;
  }

  // Outputs
  angezeigteIds = output<string[]>();
  angezeigteSpaltenwerte = output<string[]>();

  cellClick = output<CellClickEvent>();
  detailExpand = output<DetailExpandEvent>();
  dataStateChange = output<DataStateChangeEvent>();
  selectionChange = output<any[]>();

  cellValueChange = output<{ field: string; dataItem: any; value: any }>();

  onCellValueChange(field: string, dataItem: any, value: any): void {
    if (this.editMode() === 'direct') {
      dataItem[field] = value;
    } else {
      dataItem[field] = value;
      this.cellValueChange.emit({ field, dataItem, value });
    }
  }

  // Berechne Spalten mit Standardwerten und setze Typen zum Filtern
  columnsWithDefaults = computed(() => {
    const sample = this.processedData()[0] ?? {};

    return this.columns().map((col) => {
      const val = sample[col.field];
      const type = col.type ?? (val instanceof Date ? 'date' : typeof val === 'boolean' ? 'boolean' : typeof val === 'number' ? 'number' : 'string');

      return {
        ...col,
        type,
        title: col.title ?? col.field,
        width: col.width ?? 100,
        hidden: col.hidden ?? false,
        html: col.html ?? false,
        headerMarkieren: col.markieren ?? false,
        tooltip: col.tooltip ?? '',
        css: col.css ?? '',
      };
    });
  });

  // Map visible column indices to columnsWithDefaults indices
  visibleColumnIndices = computed(() => {
    const columns = this.columnsWithDefaults();
    const visibleIndices: number[] = [];
    columns.forEach((col, index) => {
      if (!col.hidden) {
        visibleIndices.push(index);
      }
    });
    return visibleIndices;
  });

  // PDF/Excel Export Icons
  public filePdfIcon: SVGIcon = filePdfIcon;
  public fileExcelIcon: SVGIcon = fileExcelIcon;
  protected DruckDatum: string = DruckDatum;

  private readonly _state = signal<State>({
    skip: 0,
    take: this.grunddaten.gridPagesize(),
    sort: [],
    filter: { logic: 'and', filters: [] },
  });
  state = this._state.asReadonly();

  // Event bei Zustandänderung im Grid
  onStateChange(e: DataStateChangeEvent) {
    this._state.update(() => ({
      ...this._state(),
      skip: e.skip,
      take: e.take,
      sort: e.sort,
    }));
    if (this.gridId() !== '') {
      this.grunddaten.saveParameter('grid_' + this.gridId(), this._state());
    }
  }

  dateiNameKomplett = computed(() => {
    return this.grunddaten.getReguliererStationName().replace('/', '_') + ' - ' + this.dateiName();
  });

  private isSelected(id: any): boolean {
    const key = String(id);
    const set = new Set((this.selectedKeysIn() ?? []).map(String));
    return set.has(key);
  }

  rowClassMerged = (ctx: RowClassArgs): { [klass: string]: boolean } => {
    const base = this.rowCallback()(ctx) || {};
    const keyField = this.selectBy();
    const id = ctx?.dataItem?.[keyField];

    return {
      ...base,
      'k-selected': this.isSelected(id),
      'grid-treffer': this.istTreffer(ctx?.dataItem),
    };
  };

  onSelectionChange(e: SelectionEvent) {
    const sel = this.selectable();
    const isMulti = typeof sel === 'object' && sel.mode === 'multiple';

    if (!isMulti) {
      // single select: just emit the clicked items (your desired behavior)
      const items = (e.selectedRows ?? []).map((r) => r.dataItem);
      this.selectionChange.emit(items);
      // also keep internal keys in sync
      const key = this.selectBy();
      this.selectedKeysIn.set(items.map((x) => x[key]));
      return;
    }

    // multi-select: accumulate keys (add selected, remove deselected)
    const key = this.selectBy();

    const add = (e.selectedRows ?? []).map((r) => r.dataItem[key]);
    const remove = new Set((e.deselectedRows ?? []).map((r) => r.dataItem[key]));

    const current = new Set(this.selectedKeysIn());
    add.forEach((k) => current.add(k));
    remove.forEach((k) => current.delete(k));

    const next = Array.from(current);
    this.selectedKeysIn.set(next);

    // emit full items for convenience
    const items = this.processedData().filter((row: any) => next.includes(row[key]));
    this.selectionChange.emit(items);
  }

  // Aktuelle Grid-Sicht (gefiltert + sortiert)
  gridView: Signal<DataResult> = computed(() =>
    this.messe('gridView (2x process)', () => {
      const state = this._state();

      const filteredSorted = process(this.processedData(), {
        filter: this.effectiveFilter(),
        sort: state.sort,
        skip: 0,
        take: Number.MAX_SAFE_INTEGER,
      });

      const paged = process(filteredSorted.data, {
        skip: state.skip,
        take: state.take,
      });

      return {
        data: paged.data,
        total: filteredSorted.total,
      };
    }),
  );

  // Gefilterte Zeilen
  gefilterteZeilen = computed(() =>
    this.messe(
      'gefilterteZeilen (process)',
      () =>
        process(this.processedData(), {
          ...this._state(),
          filter: this.effectiveFilter(),
          skip: 0,
          take: Number.MAX_SAFE_INTEGER,
        }).data,
    ),
  );

  onFilterChange(f: CompositeFilterDescriptor) {
    this._state.update((s) => ({ ...s, filter: f }));
    if (this.gridId() !== '') {
      this.grunddaten.saveParameter('grid_' + this.gridId(), this._state());
    }
  }

  private transformFilterTree(node: CompositeFilterDescriptor): CompositeFilterDescriptor {
    return {
      logic: node.logic,
      filters: (node.filters ?? []).map((f: any) => this.transformFilterNode(f)),
    };
  }

  private transformFilterNode(f: any): any {
    if ('filters' in f) {
      return this.transformFilterTree(f);
    }
    if (typeof f.value !== 'string') {
      return f;
    }

    const col = this.columnsWithDefaults().find((c) => c.field === f.field);
    const isDropdown = col?.type === 'textdropdown';

    if (f.operator === 'eq' || f.operator === 'neq') {
      return isDropdown ? f : { ...f, value: f.value.replace(/\s+/g, '') };
    }

    if (f.operator === 'contains') {
      const words = f.value.split(/\s+/).filter((w: string) => w.length > 0);
      if (words.length > 1) {
        return {
          logic: 'and',
          filters: words.map((w: string) => ({ field: f.field, operator: 'contains', value: w })),
        };
      }
      return { ...f, value: words[0] ?? '' };
    }

    return f;
  }

  private effectiveFilter = computed(() => this.transformFilterTree(this._state().filter));

  isFilterActive(field: string): boolean {
    const filter = this.state().filter;
    if (!filter || !('filters' in filter)) return false;

    return filter.filters.some((f: any) => {
      if ('field' in f) {
        return f.field === field && f.value != null && f.value !== '';
      }
      return f.filters?.some((sub: any) => sub.field === field);
    });
  }

  exportableColumns = computed(() => {
    return this.columnsWithDefaults().filter((col) => col.type !== 'button' && !col.hidden);
  });

  // Daten für Excel Export - alle gefilterten Daten ohne Paginierung
  fetchFilteredData = (): ExcelExportData => {
    const filtered = process(this.processedData(), {
      filter: this.effectiveFilter(),
      sort: this._state().sort,
      skip: 0,
      take: Number.MAX_SAFE_INTEGER,
    });
    return { data: filtered.data };
  };

  // Compute the tooltip filter dynamically based on visible columns with non-empty tooltip
  tooltipFilter = computed(() => {
    const columnsWithTooltip = this.columnsWithDefaults()
      .map((col, index) => ({ ...col, index }))
      .filter((col) => !col.hidden && col.tooltip && col.tooltip.trim() !== '');
    const filter = columnsWithTooltip
      .map((col) => {
        const visibleIndex = this.visibleColumnIndices().indexOf(col.index);
        return `.k-grid td:nth-child(${visibleIndex + 1})`;
      })
      .join(', ');
    return filter;
  });

  // Get tooltip content for a specific cell
  getTooltipContent(anchor: any): string {
    const tdElement = anchor.nativeElement.closest('td');
    if (!tdElement) return '';
    const colIndex = Array.from(tdElement.parentElement.children).indexOf(tdElement);
    const visibleColumnIndices = this.visibleColumnIndices();
    const actualColIndex = visibleColumnIndices[colIndex];
    const col = this.columnsWithDefaults()[actualColIndex];
    if (col?.tooltip) {
      const parentElement = tdElement.parentElement as HTMLTableRowElement;
      const rowIndex = parentElement.rowIndex; // Adjust for header row
      const dataItem = this.gridView().data[rowIndex];
      const content = dataItem?.[col.tooltip] ?? '';
      return content;
    }
    return '';
  }

  // Show tooltip for cells with valid tooltip content
  showTooltip(e: MouseEvent | FocusEvent): void {
    const element = (e.target as HTMLElement).closest('td');
    if (element && element.nodeName === 'TD') {
      const colIndex = Array.from(element.parentElement!.children).indexOf(element);
      const visibleColumnIndices = this.visibleColumnIndices();
      const actualColIndex = visibleColumnIndices[colIndex];
      const col = this.columnsWithDefaults()[actualColIndex];
      if (col?.tooltip && col.tooltip.trim() !== '') {
        const parentElement = element.parentElement as HTMLTableRowElement;
        const rowIndex = parentElement.rowIndex;
        const dataItem = this.gridView().data[rowIndex];
        const content = dataItem?.[col.tooltip] ?? '';
        if (content /* && element.offsetWidth < element.scrollWidth */) {
          this.tooltipDir.toggle(element);
        } else {
          this.tooltipDir.hide();
        }
      } else {
        this.tooltipDir.hide();
      }
    } else {
      this.tooltipDir.hide();
    }
  }

  onGridCellClick(event: CellClickEvent): void {
    // Prüfe ob Multiselect aktiv ist
    const sel = this.selectable();
    const isMultiselect = typeof sel === 'object' && sel.mode === 'multiple';

    // Wenn es KEIN Multiselect ist UND nicht auf die Checkbox geklickt wurde
    // dann entferne die Selektion
    if (!isMultiselect) {
      // eslint-disable-next-line no-restricted-globals
      setTimeout(() => {
        const gridElement = event.sender?.wrapper?.nativeElement;
        if (gridElement) {
          const selectedRows = gridElement.querySelectorAll('tr.k-selected, tr.k-focus, .k-table-row.k-selected');
          selectedRows.forEach((row: HTMLElement) => {
            row.classList.remove('k-selected', 'k-focus');
          });
        }
      }, 0);
    }

    // Event an Parent weitergeben
    this.cellClick.emit(event);
  }
}
